/**
 * Versioni di un'offerta: com'era prima di ogni revisione.
 *
 * Il gestionale fotografa da solo lo stato precedente ogni volta che si
 * modifica un'offerta già uscita dalla bozza — testata, contenuti e listino.
 * Qui si leggono: serve a rispondere a "cosa avevamo quotato la prima volta?"
 * senza andare a cercare il PDF nella cartella.
 */
import { useEffect, useState } from 'react';
import { offerteApi } from '../api';

const fmt = (n) => (n || n === 0)
  ? Number(n).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) : '—';
const quando = (d) => d
  ? new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric',
                                          hour: '2-digit', minute: '2-digit' })
  : '—';
const ETICHETTA_UNITA = { giorno: 'a giornata', mezza_giornata: 'a mezza giornata',
                          ora: 'a ora', intervento: 'ad attività' };

export default function VersioniOfferta({ offertaId }) {
  const [righe, setRighe] = useState(null);
  const [aperta, setAperta] = useState(null);   // versione mostrata per intero
  const [lavorando, setLavorando] = useState(false);

  const carica = () => offerteApi.versioni(offertaId).then(setRighe).catch(() => setRighe([]));
  useEffect(() => { carica(); }, [offertaId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const vedi = async (v) => {
    if (aperta?.versione === v.versione) return setAperta(null);
    try { setAperta(await offerteApi.versione(offertaId, v.versione)); } catch (e) { alert(e); }
  };

  const salvaOra = async () => {
    const nota = prompt('Una riga per ricordare cos\'è questa versione (facoltativa):');
    if (nota === null) return;
    setLavorando(true);
    try { await offerteApi.salvaVersione(offertaId, nota || null); carica(); }
    catch (e) { alert(e); } finally { setLavorando(false); }
  };

  if (!righe) return null;

  return (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
            Versioni precedenti {righe.length > 0 && `(${righe.length})`}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {righe.length === 0
              ? 'Nessuna revisione registrata. Dal momento in cui l\'offerta esce dalla bozza, ogni modifica ne conserva la versione precedente.'
              : 'Fotografia di com\'era l\'offerta prima di ogni modifica.'}
          </p>
        </div>
        <button type="button" className="btn-secondary btn-sm shrink-0" disabled={lavorando}
                title="Conserva lo stato attuale dell'offerta come versione"
                onClick={salvaOra}>
          {lavorando ? '…' : 'Salva questa versione'}
        </button>
      </div>

      {righe.map(v => (
        <div key={v.id} className="bg-white border rounded px-2.5 py-2 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-800">rev. {v.versione}</span>
            <span className="text-xs text-gray-500">{quando(v.created_at)}</span>
            {v.salvata_da && <span className="text-xs text-gray-500">· {v.salvata_da}</span>}
            <span className="text-xs text-gray-500">· {v.stato}</span>
            <span className="ml-auto font-medium text-gray-900">{fmt(v.importo)}</span>
            <button type="button" className="text-xs text-brand-600 hover:underline"
                    onClick={() => vedi(v)}>
              {aperta?.versione === v.versione ? 'chiudi' : 'vedi'}
            </button>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {v.titolo}
            {v.voci_listino > 0 && ` · ${v.voci_listino} voci a listino`}
            {v.nota && <span className="text-gray-600"> · {v.nota}</span>}
          </div>

          {aperta?.versione === v.versione && (
            <div className="mt-2 border-t pt-2 text-xs space-y-1">
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                <div><span className="text-gray-500">Numero:</span> {aperta.dati.numero || '—'}</div>
                <div><span className="text-gray-500">Cliente:</span> {aperta.dati.cliente_nome || '—'}</div>
                <div><span className="text-gray-500">Modalità:</span> {aperta.dati.modalita_esecuzione || '—'}</div>
                <div><span className="text-gray-500">Giornate previste:</span> {aperta.dati.giornate_previste ?? '—'}</div>
                <div><span className="text-gray-500">Emissione:</span> {aperta.dati.data_emissione || '—'}</div>
                <div><span className="text-gray-500">Scadenza:</span> {aperta.dati.data_scadenza || '—'}</div>
              </div>
              {(aperta.dati.listino || []).length > 0 && (
                <div className="pt-1">
                  <div className="text-gray-500 mb-0.5">Listino di allora:</div>
                  {aperta.dati.listino.map((r, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <span className="truncate">
                        {r.descrizione}
                        {Number(r.quantita_prevista) > 0 && ` — ${r.quantita_prevista}`}
                      </span>
                      <span className="shrink-0 text-gray-600">
                        {fmt(r.tariffa_cliente)} {ETICHETTA_UNITA[r.unita] || ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {aperta.dati.note && (
                <div><span className="text-gray-500">Note:</span> {aperta.dati.note}</div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
